import { InfoCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useQuery } from '@apollo/client/react';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { monoFont } from '@medcom/shared-ui/styles/globalCss.ts';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Input, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../components/MainContent';
import type { InsurancesQuery } from '../../graphql/generated/graphql.ts';
import { ellipsis } from '../../utils/ellipsis';
import { INSURANCES_QUERY } from './Insurances.gql';
import { CreateInsuranceModal } from './insurances/CreateInsuranceModal';
import { UpdateInsuranceModal } from './insurances/UpdateInsuranceModal';

export type Insurance = NonNullable<InsurancesQuery['insurances']>[number];

export const Insurances: React.FC = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editInsurance, setEditInsurance] = useState<Insurance | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);

  const { data, loading, refetch } = useQuery(INSURANCES_QUERY, {
    pollInterval: 60000,
  });

  const filteredInsurances = useMemo(
    () => filterByTokens(data?.insurances ?? [], debouncedSearch, [i => i.name, i => i.shortName, i => i.code]),
    [debouncedSearch, data]
  );

  const columns: ColumnsType<Insurance> = [
    {
      title: 'Kurzbezeichnung',
      dataIndex: 'shortName',
      key: 'shortName',
      ellipsis: true,
      width: 160,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.shortName.localeCompare(b.shortName),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Bezeichnung',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
      width: 100,
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: value => (
        <span className={monoFont}>
          <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>
        </span>
      ),
    },
    {
      title: 'Alias-Codes',
      dataIndex: 'mappings',
      key: 'mappings',
      ellipsis: true,
      width: 100,
      render: (_, record) => {
        const value = record.mappings.join(', ');
        const ellipsisValue = ellipsis(value, 30);

        return (
          <Tooltip title={value} trigger={value.length === ellipsisValue.length ? [] : ['hover']}>
            <span className={monoFont}>{ellipsisValue}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <MainContent>
      <PageHeader
        title="Krankenkassen"
        extra={[
          <Input
            key="search"
            allowClear
            autoFocus
            placeholder="Suche"
            value={search ?? ''}
            onChange={e => setSearch(e.target.value)}
            prefix={<SearchOutlined />}
            suffix={
              <Tooltip title="Suche nach Bezeichnung, Kurzbezeichnung oder Code">
                <InfoCircleOutlined />
              </Tooltip>
            }
            style={{ width: '250px' }}
          />,
          <Button key="create" icon={<PlusCircleOutlined />} type="primary" onClick={() => setCreateModalOpen(true)}>
            Neue Krankenkasse
          </Button>,
        ]}
        style={{ padding: 0, paddingBottom: 'inherit' }}
      />
      <Table<Insurance>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        sticky={true}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredInsurances}
        onRow={record => ({ onClick: () => setEditInsurance(record), style: { cursor: 'pointer' } })}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Krankenkassen`,
        }}
        loading={loading}
        columns={columns}
      />
      <CreateInsuranceModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          refetch();
        }}
        allInsurances={data?.insurances ?? []}
      />
      <UpdateInsuranceModal
        insurance={editInsurance}
        allInsurances={data?.insurances ?? []}
        onClose={() => {
          setEditInsurance(null);
          refetch();
        }}
      />
    </MainContent>
  );
};
